import { MagnifyingGlass, SpinnerGap } from '@phosphor-icons/react';

const fmt = (v) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(v || 0);
const INPUT_CLS = "w-full px-3 py-2 border border-[#D1D5DB] rounded-md text-sm focus:ring-2 focus:ring-[#0F1829]/30 focus:border-[#0F1829] outline-none";

function getTabelaPrice(p, buyerType) {
  if (buyerType === 'CS') return p.PrecoTabelaConsultor;
  if (buyerType === 'AF') return p.PrecoTabelaAfiliado;
  return p.PrecoTabelaClienteFinal;
}

function CampaignBadge({ idProduto, variant }) {
  const cls = variant === 'mobile'
    ? "ml-1 text-[9px] uppercase tracking-[0.1em] font-bold px-1 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300"
    : "ml-1.5 text-[9px] uppercase tracking-[0.15em] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300 align-middle";
  return <span data-testid={`campaign-badge${variant === 'mobile' ? '-mobile' : ''}-${idProduto}`} className={cls}>Campanha</span>;
}

function PriceCell({ p, buyerType }) {
  const isCampaign = p.EmCampanha === 'S';
  const tabela = isCampaign ? getTabelaPrice(p, buyerType) : null;
  const base = Number(p.PrecoUnitario ?? p.PrecoBaseConsultor);
  const showStrike = isCampaign && tabela && Number(tabela) > base;
  return (
    <>
      {showStrike && (
        <span className="text-[10px] text-[#9CA3AF] line-through mr-1 font-normal" data-testid={`price-strike-${p.ID_Produto}`}>
          {fmt(Number(tabela))}
        </span>
      )}
      {fmt(base)}
      {isCampaign && <CampaignBadge idProduto={p.ID_Produto} />}
    </>
  );
}

function ProductRow({ p, buyerType, qty, onSetQty, onAdd }) {
  return (
    <tr className="hover:bg-[#F9FAFB]" data-testid={`product-row-${p.ID_Produto}`}>
      <td className="px-3 py-2 text-[#6B7280]">{p.ID_Produto}</td>
      <td className="px-3 py-2 text-[#111827] font-medium">{p.Descricao}</td>
      <td className="px-3 py-2 text-right text-[#0F1829] font-semibold whitespace-nowrap">
        <PriceCell p={p} buyerType={buyerType} />
      </td>
      <td className="px-3 py-2 text-center">
        <input type="number" min="0" value={qty} data-testid={`qty-${p.ID_Produto}`}
          onChange={e => onSetQty(p.ID_Produto, e.target.value)}
          className="w-16 px-2 py-1 text-center border border-[#D1D5DB] rounded text-sm outline-none mx-auto" />
      </td>
      <td className="px-3 py-2 text-right font-semibold text-[#374151] whitespace-nowrap">
        {qty > 0 ? fmt(qty * Number(p.PrecoUnitario ?? p.PrecoBaseConsultor)) : '-'}
      </td>
      <td className="px-3 py-2 text-center">
        <button onClick={() => onAdd(p)} disabled={qty < 1} data-testid={`add-btn-${p.ID_Produto}`}
          className="px-3 py-1.5 bg-[#0F1829] text-white text-xs font-medium rounded hover:bg-[#001f6e] disabled:opacity-30 transition-colors whitespace-nowrap">
          Adicionar
        </button>
      </td>
    </tr>
  );
}

function ProductCard({ p, buyerType, qty, onSetQty, onAdd }) {
  const total = qty > 0 ? qty * Number(p.PrecoUnitario ?? p.PrecoBaseConsultor) : 0;
  const isCampaign = p.EmCampanha === 'S';
  return (
    <div data-testid={`product-card-${p.ID_Produto}`} className="border border-[#E5E7EB] rounded-lg p-3 bg-white">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] text-[#9CA3AF] leading-tight">#{p.ID_Produto}</p>
          <p className="text-sm font-medium text-[#111827] leading-snug break-words">{p.Descricao}</p>
        </div>
        <span className="text-sm font-semibold text-[#0F1829] whitespace-nowrap flex-shrink-0">
          {fmt(p.PrecoUnitario ?? p.PrecoBaseConsultor)}
          {isCampaign && <CampaignBadge idProduto={p.ID_Produto} variant="mobile" />}
        </span>
      </div>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <label className="text-xs text-[#6B7280]">Qtd.</label>
          <input type="number" min="0" value={qty} data-testid={`qty-${p.ID_Produto}`}
            onChange={e => onSetQty(p.ID_Produto, e.target.value)}
            className="w-16 px-2 py-1.5 text-center border border-[#D1D5DB] rounded text-sm outline-none" />
          {qty > 0 && <span className="text-xs text-[#6B7280] whitespace-nowrap">= <strong className="text-[#111827]">{fmt(total)}</strong></span>}
        </div>
        <button onClick={() => onAdd(p)} disabled={qty < 1} data-testid={`add-btn-${p.ID_Produto}`}
          className="px-4 py-2 bg-[#0F1829] text-white text-xs font-semibold rounded hover:bg-[#001f6e] disabled:opacity-30 transition-colors whitespace-nowrap">
          Adicionar
        </button>
      </div>
    </div>
  );
}

/**
 * Product search form + results — supports both desktop (table) and mobile
 * (cards). Buyer type (CF/CS/AF) controls which "table price" is used for
 * showing a campaign strike-through discount.
 */
export default function ProductSearchSection({
  productIdSearch, setProductIdSearch,
  productNameSearch, setProductNameSearch,
  onSearch, loadingProducts, searchTriggered,
  products, buyerType,
  productQuantities, onSetQty, onAddToCart,
}) {
  return (
    <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm mb-6" data-testid="section-product-search">
      <div className="px-5 py-3 border-b border-[#E5E7EB] bg-[#F9FAFB] rounded-t-xl">
        <h2 className="text-sm font-semibold text-[#111827]">Pesquisa de Produtos</h2>
      </div>
      <div className="p-5">
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="flex-1">
            <label className="block text-xs font-medium text-[#6B7280] mb-1">ID do Produto</label>
            <input data-testid="search-product-id" type="text" value={productIdSearch}
              onChange={e => setProductIdSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && onSearch()}
              placeholder="ID..." className={INPUT_CLS} />
          </div>
          <div className="flex-1">
            <label className="block text-xs font-medium text-[#6B7280] mb-1">Nome do Produto</label>
            <input data-testid="search-product-name" type="text" value={productNameSearch}
              onChange={e => setProductNameSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && onSearch()}
              placeholder="Nome do produto..." className={INPUT_CLS} />
          </div>
          <div className="flex items-end">
            <button onClick={onSearch} data-testid="search-products-btn" disabled={loadingProducts}
              className="flex items-center gap-2 px-6 py-2 bg-[#0F1829] text-white text-sm font-medium rounded-md hover:bg-[#001f6e] disabled:opacity-40 transition-colors whitespace-nowrap">
              {loadingProducts ? <SpinnerGap size={16} className="animate-spin" /> : <MagnifyingGlass size={16} />} Pesquisar
            </button>
          </div>
        </div>

        {searchTriggered && (
          <>
            {/* Desktop: table */}
            <div className="hidden sm:block border border-[#E5E7EB] rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]" data-testid="products-table">
                  <thead><tr className="bg-[#F9FAFB] text-[#6B7280] text-xs uppercase tracking-wide">
                    <th className="px-3 py-2 text-left font-medium w-20">ID</th>
                    <th className="px-3 py-2 text-left font-medium">Descrição</th>
                    <th className="px-3 py-2 text-right font-medium w-24">Preço</th>
                    <th className="px-3 py-2 text-center font-medium w-24">Quantidade</th>
                    <th className="px-3 py-2 text-right font-medium w-24">Total</th>
                    <th className="px-3 py-2 text-center font-medium w-28"></th>
                  </tr></thead>
                  <tbody className="divide-y divide-[#E5E7EB]">
                    {products.length === 0 && (
                      <tr><td colSpan={6} className="px-4 py-6 text-center text-[#9CA3AF]">Nenhum produto encontrado.</td></tr>
                    )}
                    {products.map(p => (
                      <ProductRow key={p.ID_Produto} p={p} buyerType={buyerType}
                        qty={productQuantities[p.ID_Produto] || 0}
                        onSetQty={onSetQty} onAdd={onAddToCart} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile: cards */}
            <div className="sm:hidden space-y-2">
              {products.length === 0 && (
                <div className="p-6 text-center text-[#9CA3AF] border border-[#E5E7EB] rounded-lg">Nenhum produto encontrado.</div>
              )}
              {products.map(p => (
                <ProductCard key={p.ID_Produto} p={p} buyerType={buyerType}
                  qty={productQuantities[p.ID_Produto] || 0}
                  onSetQty={onSetQty} onAdd={onAddToCart} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
